import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { OnlineDot } from "@/components/OnlineDot";
import { Check, X, MessageSquare, FileText, MapPin, CalendarDays, Clock, User } from "lucide-react";

const statusStyle = (s) =>
  s === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : s === "declined" ? "bg-red-50 text-red-600 border-red-200"
      : "bg-slate-50 text-slate-600 border-slate-200";

const fmtDate = (d) => {
  if (!d) return "Flexible";
  const parsed = new Date(d);
  return isNaN(parsed) ? d : parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="ml-auto text-xs font-semibold text-foreground">{value}</span>
  </div>
);

export function OfferCard({ offer: o, index = 0, onRespond, onSign, onMessage }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`offer-card-${o.id}`}
      className="bg-white border border-border rounded-2xl overflow-hidden flex flex-col hover:shadow-md transition-shadow"
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-border">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display font-semibold truncate">{o.title}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
              <MapPin className="w-3 h-3 shrink-0" /> {o.address}
            </div>
          </div>
          <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize ${statusStyle(o.status)}`}>{o.status}</span>
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="font-display text-3xl font-extrabold tracking-tight text-slate-900">${o.rent?.toLocaleString()}</span>
          <span className="text-sm text-muted-foreground">/mo</span>
          <span className="ml-auto text-xs text-muted-foreground">Deposit ${o.deposit?.toLocaleString()}</span>
        </div>
      </div>

      {/* Meta */}
      <div className="px-5 py-4 space-y-2.5">
        <Meta icon={Clock} label="Lease" value={`${o.lease_length_months} months`} />
        <Meta icon={CalendarDays} label="Move-in" value={fmtDate(o.move_in_date)} />
        <div className="flex items-center gap-2">
          <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground">From</span>
          <span className="ml-auto flex items-center gap-2">
            <span className="text-xs font-semibold text-foreground">{o.landlord_name}</span>
            <OnlineDot online={o.landlord_online} showLabel={false} />
          </span>
        </div>
        {o.landlord_online && (
          <div className="text-[11px] text-[#1f9e0e] font-semibold text-right -mt-1">Online now — great time for a quick reply</div>
        )}
      </div>

      {o.incentives?.length > 0 && (
        <div className="px-5 pb-3 flex flex-wrap gap-1.5">
          {o.incentives.map((inc, j) => (<span key={`${o.id}-inc-${j}`} className="text-[11px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{inc}</span>))}
        </div>
      )}
      {o.notes && <p className="px-5 pb-3 text-sm text-muted-foreground line-clamp-3">{o.notes}</p>}

      {/* Actions */}
      <div className="mt-auto px-5 pb-5 pt-1 space-y-2">
        {["sent", "viewed"].includes(o.status) && (
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid={`accept-${o.id}`} size="sm" onClick={() => onRespond(o.id, "accept")}><Check className="w-4 h-4 mr-1" /> Accept</Button>
            <Button data-testid={`decline-${o.id}`} size="sm" variant="outline" onClick={() => onRespond(o.id, "decline")}><X className="w-4 h-4 mr-1" /> Decline</Button>
          </div>
        )}
        {o.status === "accepted" && (
          <>
            <Button size="sm" variant="outline" className="w-full" onClick={onMessage}><MessageSquare className="w-4 h-4 mr-1" /> Message landlord</Button>
            {o.lease_content ? (
              o.lease_signed ? (
                <div className="text-center text-sm text-emerald-700 font-semibold flex items-center justify-center gap-1"><Check className="w-4 h-4" /> Lease signed</div>
              ) : (
                <Button size="sm" className="w-full" data-testid={`sign-${o.id}`} onClick={() => onSign(o)}><FileText className="w-4 h-4 mr-1" /> Review & sign lease</Button>
              )
            ) : (
              <div className="text-center text-xs text-muted-foreground">Awaiting lease from landlord…</div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}
